import { useMemo, useState, type MouseEvent } from "react";
import {
	Box,
	CircularProgress,
	GlobalStyles,
	IconButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Tooltip,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FormatListBulletedSharpIcon from "@mui/icons-material/FormatListBulletedSharp";
import { AgGridReact } from "ag-grid-react";
import type {
	ColDef,
	GridApi,
	GridReadyEvent,
	ICellRendererParams,
	RowClickedEvent,
} from "ag-grid-community";
import { formatProxyDate } from "@/shared/utils/proxyDateTime";
import type { RollListItem } from "../models/RollshopOperations";

const CENTER_CELL = { textAlign: "center" } as const;
// Center + force single-line with ellipsis (some catalog values like
// "AMERICAN ROLL GROUP" or "PEARLITIC NODULAR" overflow narrow columns
// and used to wrap, breaking the 30px row height).
const CENTER_CELL_NOWRAP = {
	textAlign: "center",
	whiteSpace: "nowrap",
	overflow: "hidden",
	textOverflow: "ellipsis",
} as const;
const CENTER_HEADER = "ag-cell-center-header";

const fmtNum2 = (v: number | null | undefined) =>
	v == null ? "" : Number(v).toFixed(2);

interface ActionsCellProps<T> extends ICellRendererParams<T> {
	onDelete: (row: T) => void;
	deleteDisabled?: boolean;
	deleteDisabledReason?: string;
}

// Actions menu cell — pinned-right column. Click the list icon to open a
// MUI Menu with available row actions. For now the only entry is "Delete";
// add more MenuItem entries here as Cut / Edit row / etc. land.
//
// Pattern mirrored from roanoke-cmms /work-orders (FormatListBulletedSharpIcon
// + popover menu). We use plain MUI <Menu> instead of AG Grid's enterprise
// context menu so the project-wide `suppressContextMenu` rule keeps standing.
export const ActionsCell = <T,>({
	data,
	onDelete,
	deleteDisabled = false,
	deleteDisabledReason,
}: ActionsCellProps<T>) => {
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const open = Boolean(anchorEl);

	if (!data) return null;

	const handleOpen = (e: MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		setAnchorEl(e.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleDeleteClick = (e: MouseEvent<HTMLLIElement>) => {
		e.stopPropagation();
		setAnchorEl(null);
		onDelete(data);
	};

	return (
		<Box sx={{ display: "flex", justifyContent: "center", height: "100%" }}>
			<Tooltip title="Actions" placement="left">
				<IconButton
					size="small"
					onClick={handleOpen}
					sx={{ p: 0.25 }}
					aria-label="row actions"
				>
					<FormatListBulletedSharpIcon fontSize="small" color="action" />
				</IconButton>
			</Tooltip>
			<Menu
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				slotProps={{ paper: { sx: { minWidth: 160 } } }}
				// Keep clicks inside the menu from bubbling up to the row click
				// handler (which would re-select the row).
				onClick={(e) => e.stopPropagation()}
			>
				<Tooltip
					title={deleteDisabled ? deleteDisabledReason : ""}
					placement="left"
					disableHoverListener={!deleteDisabled}
				>
					{/* span wrapper so the Tooltip still fires on a disabled MenuItem. */}
					<span>
						<MenuItem
							onClick={handleDeleteClick}
							disabled={deleteDisabled}
							sx={{
								color: deleteDisabled ? "action.disabled" : "error.main",
							}}
						>
							<ListItemIcon>
								<DeleteOutlineIcon
									fontSize="small"
									sx={{
										color: deleteDisabled ? "action.disabled" : "error.main",
									}}
								/>
							</ListItemIcon>
							<ListItemText>Delete</ListItemText>
						</MenuItem>
					</span>
				</Tooltip>
			</Menu>
		</Box>
	);
};

// Labels match the legacy /rolls grid. Numeric columns stay narrow so Roll App
// fits its longest catalog name (30 chars) on one line. Supplier flexes to
// absorb remaining space.
const buildColumnDefs = (): ColDef<RollListItem>[] => [
	{
		field: "Name",
		headerName: "Name",
		width: 80,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "Diameter",
		headerName: "Diam",
		width: 60,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => (p.value == null ? "" : Number(p.value).toFixed(3)),
	},
	{
		field: "MillSizeName",
		headerName: "Mill Size",
		width: 70,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "RollApplicationName",
		headerName: "Roll App",
		width: 225,
		cellStyle: CENTER_CELL_NOWRAP,
		headerClass: CENTER_HEADER,
		tooltipField: "RollApplicationName",
	},
	{
		field: "BlankDiameter",
		headerName: "Blank Diam",
		width: 80,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => (p.value == null ? "" : Number(p.value).toFixed(3)),
	},
	{
		field: "Hardness",
		headerName: "Hardness",
		width: 70,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "RollMaterialName",
		headerName: "Roll Material",
		width: 150,
		cellStyle: CENTER_CELL_NOWRAP,
		headerClass: CENTER_HEADER,
		tooltipField: "RollMaterialName",
	},
	{
		field: "Cost",
		headerName: "Cost",
		width: 70,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => fmtNum2(p.value),
	},
	{
		field: "PurchaseOrderName",
		headerName: "PO",
		width: 90,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "PurchaseOrderItemNumber",
		headerName: "PO Item",
		width: 60,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "SupplierName",
		headerName: "Supplier",
		flex: 1.4,
		minWidth: 180,
		cellStyle: CENTER_CELL_NOWRAP,
		headerClass: CENTER_HEADER,
		tooltipField: "SupplierName",
	},
	{
		field: "SupplierInvoiceNumber",
		headerName: "Supplier Invoice No.",
		width: 130,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "CostCenterName",
		headerName: "G/L Cost Center",
		width: 125,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "OrderStatusName",
		headerName: "Order Status",
		width: 100,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "OrderTimestamp",
		headerName: "Order Date",
		width: 95,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => formatProxyDate(p.value),
	},
	{
		field: "ReceiveTimestamp",
		headerName: "Receive Date",
		width: 100,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => formatProxyDate(p.value),
		sort: "desc",
	},
	{
		field: "InvoiceTimestamp",
		headerName: "Invoice Date",
		width: 100,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => formatProxyDate(p.value),
	},
	{
		field: "StatusName",
		headerName: "Status",
		width: 90,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "Returned",
		headerName: "Returned",
		width: 95,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => formatProxyDate(p.value),
	},
	{
		field: "ScrapWeight",
		headerName: "Scrap Weight",
		width: 105,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
];

interface Props {
	rows: RollListItem[];
	isLoading: boolean;
	// Optional master-detail integration. When omitted, the grid renders
	// in plain full-screen mode (no row selection, no pointer cursor).
	selectedId?: number | null;
	onRowClick?: (row: RollListItem) => void;
	// Bubble up the grid api so the parent can drive Excel export.
	onGridReady?: (api: GridApi<RollListItem>) => void;
	// When provided, an Actions column with a Delete icon is appended to
	// the right of the grid. The button is disabled when `deleteDisabled`
	// is true (e.g. while the page is in legacy proxy mode).
	onDelete?: (row: RollListItem) => void;
	deleteDisabled?: boolean;
	deleteDisabledReason?: string;
}

export const RollsTable = ({
	rows,
	selectedId = null,
	onRowClick,
	isLoading,
	onGridReady,
	onDelete,
	deleteDisabled = false,
	deleteDisabledReason = "Disabled",
}: Props) => {
	const columnDefs = useMemo(() => {
		const base = buildColumnDefs();
		if (!onDelete) return base;
		const actionsCol: ColDef<RollListItem> = {
			colId: "Actions",
			headerName: "",
			width: 50,
			pinned: "right",
			sortable: false,
			resizable: false,
			cellStyle: { padding: 0 },
			cellRenderer: ActionsCell,
			cellRendererParams: {
				onDelete,
				deleteDisabled,
				deleteDisabledReason,
			},
		};
		return [...base, actionsCol];
	}, [onDelete, deleteDisabled, deleteDisabledReason]);

	const selectionEnabled = Boolean(onRowClick);

	const handleRowClicked = onRowClick
		? (e: RowClickedEvent<RollListItem>) => {
				if (e.data) onRowClick(e.data);
			}
		: undefined;

	const handleGridReady = (e: GridReadyEvent<RollListItem>) => {
		if (selectionEnabled && selectedId != null) {
			const node = e.api.getRowNode(String(selectedId));
			if (node) node.setSelected(true);
		}
		onGridReady?.(e.api);
	};

	return (
		<Box sx={{ position: "relative", height: "100%" }}>
			<GlobalStyles
				styles={{
					".ag-cell-center-header .ag-header-cell-label": {
						justifyContent: "center",
					},
					".ag-row-selected, .ag-row-selected:hover": {
						backgroundColor: "rgba(25, 118, 210, 0.12) !important",
					},
				}}
			/>
			{isLoading && rows.length === 0 && (
				<Box
					sx={{
						position: "absolute",
						inset: 0,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						zIndex: 1,
						bgcolor: "rgba(255,255,255,0.7)",
					}}
				>
					<CircularProgress />
				</Box>
			)}
			<AgGridReact<RollListItem>
				columnDefs={columnDefs}
				rowData={rows}
				defaultColDef={{
					resizable: true,
					sortable: true,
				}}
				suppressContextMenu
				headerHeight={32}
				rowHeight={30}
				getRowId={(p) => String(p.data.Id)}
				onRowClicked={handleRowClicked}
				rowSelection={
					selectionEnabled
						? {
								mode: "singleRow",
								checkboxes: false,
								enableClickSelection: true,
							}
						: undefined
				}
				rowStyle={selectionEnabled ? { cursor: "pointer" } : undefined}
				onGridReady={handleGridReady}
			/>
		</Box>
	);
};
